import {
  getEventStoppageType,
  isStoppageTypeForNewWithCodeOldWithReason,
  isStoppageTypeWithCode,
  isStoppageTypeWithReason
} from '@domain/shifthandover'
import {
  CategoryDropDown,
  EventHighlight,
  EventProcessStageAutocomplete,
  EventStoppageTypeDropDown,
  PriorityDropDown,
  ProcessStageDropDown,
  SimpleDateTimePicker
} from '@hconnect/common/components/shiftEventFormFields'
import {DEPARTMENT_BY_TYPE, INPUT_FORM_FIELD_SX, REGULAR_SIZE} from '@hconnect/common/consts'
import {stoppageToggleButtonGroupSx} from '@hconnect/common/styles'
import {
  DisabledFields,
  EquipmentIdType,
  EquipmentRunningTimes,
  HiddenFields,
  Priority,
  shifthandoverStoppagesList,
  Stoppage,
  StoppageKind,
  StoppageType,
  DowntimeStoppageCodes
} from '@hconnect/common/types'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import {Box, Grid, TextField, ToggleButton, ToggleButtonGroup} from '@mui/material'
import {isNil} from 'lodash'
import React, {FC, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_SHIFT_EVENT_WIDGET_NAMESPACE} from '../consts'
import {RunningTimeDropDownItem, RunningTimesData} from '../types'
import {getRunningTimeDropdownItems} from '../utils'

import {EventTypeTag} from './EventTypeTag'
import {
  EventDescriptionBox,
  EventStoppageCodesDropDown,
  SalesImpact,
  EquipmentSearchContainer,
  StoppedMainEquipmentContainer,
  StoppageTimeDropDown
} from './shift-event-form-fields'

type PossibleErrors =
  | keyof Stoppage
  | 'stoppageCode'
  | 'stoppageReason'
  | 'equipmentNumber'
  | 'mainEquipmentNumbers'
  | 'stoppedMainEquipmentNumbers'

type StoppageFormFieldsProps = {
  item: Partial<Stoppage>
  updateMultiPart: (delta: Partial<Stoppage>) => void
  validationError: Map<PossibleErrors, string>
  timezone: string
  plantId: string
  runningTimesData?: RunningTimesData<EquipmentRunningTimes>
  stoppageCodes: DowntimeStoppageCodes
  disabledFields?: DisabledFields
  hiddenFields?: HiddenFields
  equipmentIdType?: EquipmentIdType
  autocompleteProcessStage?: boolean
  children: React.ReactNode
  isFunctionalLocationRequired?: boolean
}

enum DateSelectionType {
  List = 'list',
  Manual = 'manual'
}

// eslint-disable-next-line complexity
export const StoppageFormFields: FC<StoppageFormFieldsProps> = ({
  item,
  updateMultiPart,
  validationError,
  children,
  timezone,
  plantId,
  runningTimesData,
  stoppageCodes,
  equipmentIdType,
  disabledFields,
  hiddenFields,
  autocompleteProcessStage,
  isFunctionalLocationRequired
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(COMMON_SHIFT_EVENT_WIDGET_NAMESPACE)
  const [dateSelectionType, setDateSelectionType] = useState<DateSelectionType>(
    DateSelectionType.Manual
  )
  const [selectedRunningTimeId, setSelectedRunningTimeId] = useState<string>()

  const runningTimesList: RunningTimeDropDownItem[] = useMemo(
    () => getRunningTimeDropdownItems(runningTimesData?.data || [], language, t),
    [language, runningTimesData?.data, t]
  )

  const getErrorText = (key: PossibleErrors) => {
    const errorCode = validationError.get(key)
    return errorCode && t(errorCode)
  }

  const onStoppageTypeChange = (type: StoppageType) => {
    if (type === item.stoppageType) return

    updateMultiPart({
      upmDepartmentId: undefined
    })

    if (isStoppageTypeForNewWithCodeOldWithReason(type)) {
      updateMultiPart({
        stoppageType: 'other',
        stoppageReason: undefined
      })
      updateMultiPart({
        stoppageType: type,
        stoppageCode: undefined
      })
      return
    }

    // remove the left over from the previous type
    if (isStoppageTypeWithCode(type)) {
      updateMultiPart({
        stoppageType: 'other',
        stoppageReason: undefined
      })
      updateMultiPart({
        stoppageType: type,
        stoppageCode: undefined
      })
    } else {
      updateMultiPart({
        stoppageType: 'kiln',
        stoppageCode: undefined
      })
      updateMultiPart({
        stoppageType: type,
        stoppageReason: undefined
      })
    }
  }

  return (
    <>
      <Box display="flex" gap={2} mb={2}>
        {!hiddenFields?.title && (
          <TextField
            data-test-id="event-form-title"
            error={validationError.has('title')}
            fullWidth={true}
            helperText={getErrorText('title')}
            label={t('shiftEvent.label.title')}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateMultiPart({title: event.target.value})
            }
            required
            disabled={disabledFields?.title}
            value={item.title}
            variant="filled"
            InputProps={INPUT_FORM_FIELD_SX}
          />
        )}

        {!hiddenFields?.isHighlighted && (
          <EventHighlight
            data-test-id="event-form-isHighlighted"
            title={t('eventsTable.action.highlight')}
            value={item.isHighlighted}
            disabled={disabledFields?.isHighlighted}
            onChange={(value) => updateMultiPart({isHighlighted: value})}
          />
        )}
      </Box>

      <Box mb={1} mt={2}>
        {!hiddenFields?.eventType && (
          <ToggleButtonGroup
            color={'primary'}
            sx={stoppageToggleButtonGroupSx}
            fullWidth
            exclusive
            disabled={disabledFields?.eventType}
            value={item.eventType}
            onChange={(event: React.MouseEvent<HTMLElement>, value: StoppageKind) => {
              if (value) updateMultiPart({eventType: value, stoppageCode: undefined})
            }}
          >
            {shifthandoverStoppagesList.map((codeType) => (
              <ToggleButton
                key={codeType}
                value={codeType}
                data-test-id={`event-form-stoppage-code-type-${codeType}`}
              >
                {t(`shiftEvent.stoppage.${codeType}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <Grid container my={0.5} spacing={2} alignItems="flex-start">
          {!hiddenFields?.stoppageCode && (
            <Grid item xs={12}>
              <EventStoppageTypeDropDown
                translate={t}
                data-test-id="event-form-stoppageType"
                value={item.stoppageType ?? 'other'}
                fullWidth={true}
                required
                disabled={disabledFields?.stoppageType}
                onChange={onStoppageTypeChange}
              />
            </Grid>
          )}

          <StoppedMainEquipmentContainer
            errorText={getErrorText('stoppedMainEquipmentNumbers')}
            plantId={plantId}
            updateMultiPart={updateMultiPart}
            item={item}
            disabled={disabledFields?.upmDepartmentId}
            availableDepartmentCodes={item.stoppageType && DEPARTMENT_BY_TYPE[item.stoppageType]}
          />

          {!hiddenFields?.hasSalesImpact && (
            <SalesImpact
              item={item}
              update={updateMultiPart}
              costErrorText={getErrorText('cost')}
              currencyErrorText={getErrorText('costCurrency')}
            />
          )}

          {item.stoppageType &&
          isStoppageTypeWithCode(item.stoppageType) &&
          'stoppageCode' in item ? (
            <Grid item xs={12}>
              <EventStoppageCodesDropDown
                errorText={getErrorText('stoppageCode')}
                onChange={(code) => {
                  if (code?.stoppageCategory) {
                    updateMultiPart({
                      stoppageType: item.stoppageType,
                      stoppageCode: code?.code,
                      category: code.stoppageCategory
                    })
                  } else {
                    updateMultiPart({
                      stoppageType: item.stoppageType,
                      stoppageCode: code?.code
                    })
                  }
                }}
                kilnStoppageCodes={stoppageCodes.kilnStoppageCodes}
                rawMillStoppageCodes={stoppageCodes.rawMillStoppageCodes}
                finishMillStoppageCodes={stoppageCodes.finishMillStoppageCodes}
                alternativeFuelInstallationStoppageCodes={
                  stoppageCodes.alternativeFuelInstallationStoppageCodes
                }
                value={item.stoppageCode}
                stoppageType={item.stoppageType}
                stoppageKind={item.eventType}
                required={isStoppageTypeWithCode(item.stoppageType)}
                disabled={disabledFields?.stoppageCode}
              />
            </Grid>
          ) : (
            item.stoppageType &&
            isStoppageTypeWithReason(item.stoppageType) &&
            'stoppageReason' in item && (
              <Grid item xs={12}>
                <TextField
                  data-test-id="event-form-stoppageReason"
                  error={validationError.has('stoppageReason')}
                  fullWidth={true}
                  helperText={getErrorText('stoppageReason')}
                  label={t('shiftEvent.label.stoppageReason')}
                  multiline
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateMultiPart({
                      stoppageType: item.stoppageType,
                      stoppageReason: event.target.value
                    })
                  }
                  required
                  minRows={3}
                  maxRows={10}
                  value={item.stoppageReason ?? ''}
                  inputProps={{'data-test-id': 'event-form-stoppageReason-input'}}
                />
              </Grid>
            )
          )}

          {!hiddenFields?.stoppageType && item.eventType && (
            <EventTypeTag
              eventType={item.eventType}
              sx={{height: 72, display: 'flex', alignItems: 'center'}}
            />
          )}

          {runningTimesData && (
            <Grid item xs={12}>
              <ToggleButtonGroup
                color={'primary'}
                sx={stoppageToggleButtonGroupSx}
                fullWidth
                exclusive
                value={dateSelectionType}
                onChange={(event: React.MouseEvent<HTMLElement>, value: DateSelectionType) => {
                  if (!isNil(value)) setDateSelectionType(value)
                }}
                disabled={disabledFields?.stoppageDateType}
              >
                {[DateSelectionType.List, DateSelectionType.Manual].map((selectionType) => (
                  <ToggleButton
                    key={selectionType}
                    value={selectionType}
                    data-test-id={`event-form-stoppage-date-type-${selectionType}`}
                    sx={{gap: 0.75}}
                  >
                    {DateSelectionType.List === selectionType ? (
                      <FormatListBulletedIcon />
                    ) : (
                      <CalendarTodayOutlinedIcon />
                    )}
                    {t(`shiftEvent.stoppage.${selectionType}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>
          )}
          {dateSelectionType === DateSelectionType.Manual ? (
            <>
              <Grid item xs={12}>
                {/* TODO add visual for an validation error  */}
                <SimpleDateTimePicker
                  label={t('shiftEvent.stoppage.stoppageStart')}
                  date={item.stoppageStart ? item.stoppageStart : null}
                  errorText={validationError.get('stoppageStart')}
                  required={true}
                  timezone={timezone}
                  handleDateChange={(date) => {
                    updateMultiPart({
                      stoppageStart: date || undefined,
                      pxTrendTag: undefined,
                      pxTrendStoppage: undefined
                    })
                  }}
                  disabled={disabledFields?.stoppageStart}
                />
              </Grid>
              <Grid item xs={12}>
                {/* TODO add visual for an validation error  */}
                <SimpleDateTimePicker
                  timezone={timezone}
                  label={t('shiftEvent.stoppage.stoppageEnd')}
                  errorText={validationError.get('stoppageEnd')}
                  date={item.stoppageEnd ? item.stoppageEnd : null}
                  handleDateChange={(date) => {
                    updateMultiPart({
                      stoppageEnd: date || undefined,
                      pxTrendTag: undefined,
                      pxTrendStoppage: undefined
                    })
                  }}
                  disabled={disabledFields?.stoppageEnd}
                />
              </Grid>
            </>
          ) : runningTimesData ? (
            <Grid item xs={12} zeroMinWidth>
              <StoppageTimeDropDown
                translate={t}
                runningTimesData={{
                  ...runningTimesData,
                  data: runningTimesList
                }}
                value={selectedRunningTimeId}
                errorText={getErrorText('stoppageStart') || validationError.get('stoppageStart')}
                data-test-id="event-form-stoppage-time"
                onChange={(id: string) => {
                  setSelectedRunningTimeId(id)
                  const runningTimeItem = runningTimesList.find((rt) => rt.id === id)
                  if (runningTimeItem) {
                    updateMultiPart({
                      stoppageType: getEventStoppageType(runningTimeItem.equipment),
                      stoppageStart: runningTimeItem.runningTime.beginTechnical,
                      stoppageEnd: runningTimeItem.runningTime.endTechnical,
                      pxTrendTag: runningTimeItem.equipment.id,
                      pxTrendStoppage: runningTimeItem.runningTime
                    })
                    onStoppageTypeChange(getEventStoppageType(runningTimeItem.equipment))
                  }
                }}
              />
            </Grid>
          ) : (
            <></>
          )}

          {!hiddenFields?.category && (
            <Grid item {...REGULAR_SIZE}>
              <CategoryDropDown
                translate={t}
                data-test-id="event-form-category"
                errorText={getErrorText('category')}
                onChange={(value) => updateMultiPart({category: value})}
                value={item.category}
              />
            </Grid>
          )}
          {!hiddenFields?.priority && (
            <Grid item {...REGULAR_SIZE}>
              <PriorityDropDown
                translate={t}
                data-test-id="event-form-priority"
                errorText={getErrorText('priority')}
                onChange={(value) => updateMultiPart({priority: value})}
                value={item.priority as Priority}
              />
            </Grid>
          )}
          {!hiddenFields?.mainEquipment && (
            <Grid item xs={12} mt={1.5}>
              <EquipmentSearchContainer
                data-test-id="event-form-equipmentNumber"
                equipmentNumberErrorText={getErrorText('equipmentNumber')}
                mainEquipmentNumbersErrorText={getErrorText('mainEquipmentNumbers')}
                updateMultiPart={updateMultiPart}
                // TODO adjust when HCP-102633 is worked upon
                isFunctionalLocationRequired={!!isFunctionalLocationRequired}
                // TODO adjust when HCP-102633 is worked upon
                value={item as Stoppage}
                plantId={plantId}
                equipmentDisabled={disabledFields?.equipment}
                mainEquipmentDisabled={disabledFields?.mainEquipment}
                equipmentIdType={equipmentIdType}
                hideFunctionalLocation={hiddenFields?.equipmentNumber}
              />
            </Grid>
          )}
          {!hiddenFields?.processStage &&
            (autocompleteProcessStage ? (
              <Grid item xs={12}>
                <EventProcessStageAutocomplete
                  plantId={plantId}
                  data-test-id="event-form-processStage"
                  disabled={disabledFields?.processStage}
                  errorText={getErrorText('processStage')}
                  onChange={(value) => updateMultiPart({processStage: value})}
                  value={item.processStage}
                />
              </Grid>
            ) : (
              <Grid item {...REGULAR_SIZE}>
                <ProcessStageDropDown
                  translate={t}
                  disabled={disabledFields?.processStage}
                  plantId={plantId}
                  data-test-id="event-form-processStage"
                  errorText={getErrorText('processStage')}
                  onChange={(value) => updateMultiPart({processStage: value})}
                  value={item.processStage}
                />
              </Grid>
            ))}
          {!hiddenFields?.description && (
            <Grid item xs={12} mt={1}>
              <EventDescriptionBox
                current={item.description || ''}
                errorCode={validationError.get('description')}
                onChange={(description) => updateMultiPart({description})}
                required
                disabled={disabledFields?.description}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {children}
    </>
  )
}
